import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User } from "lucide-react";
import { cn } from "@/lib/utils";
import genericPersonImage from "@/assets/generic-person.png";

interface UserAvatarProps {
  src?: string | null;
  alt?: string;
  fallbackText?: string;
  className?: string;
  size?: "sm" | "md" | "lg";
  name?: string;
  email?: string;
  singleInitial?: boolean;
}

// Generate initials from name or email
const getInitials = (name?: string, email?: string, singleInitial?: boolean): string => {
  if (name && name.trim()) {
    const parts = name.trim().split(' ');
    
    // If single initial is requested, prioritize last name's first letter
    if (singleInitial) {
      // Prioritize last name's first letter if available
      if (parts.length >= 2) {
        return parts[parts.length - 1].charAt(0).toUpperCase();
      }
      return parts[0].charAt(0).toUpperCase();
    }
    
    // For regular behavior, use first and last name initials
    if (parts.length >= 2) {
      const initials = parts[0].charAt(0) + parts[parts.length - 1].charAt(0);
      // Auto-detect if we should use single initial for very long names
      if (initials.length > 2) {
        return parts[0].charAt(0).toUpperCase();
      }
      return initials.toUpperCase();
    }
    return parts[0].charAt(0).toUpperCase();
  }
  
  if (email) {
    return email.charAt(0).toUpperCase();
  }
  
  return "";
};

// Generate a professional color based on text
const getAvatarColor = (text?: string): string => {
  if (!text) return "hsl(var(--muted))";
  
  // Professional, muted color palette using design system tokens
  const colors = [
    "hsl(240 10% 15%)", // dark slate
    "hsl(240 8% 46%)",  // muted gray
    "hsl(220 14% 36%)", // professional blue-gray
    "hsl(210 12% 42%)", // slate blue
    "hsl(200 10% 38%)", // muted blue
    "hsl(225 8% 44%)",  // neutral blue-gray
    "hsl(215 16% 40%)", // professional steel
    "hsl(240 6% 48%)",  // warm gray
  ];
  
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = text.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

export function UserAvatar({ 
  src, 
  alt = "User avatar", 
  fallbackText,
  className,
  size = "md",
  name,
  email,
  singleInitial = true
}: UserAvatarProps) {
  const sizeClasses = {
    sm: "h-8 w-8 text-xs",
    md: "h-10 w-10 text-sm", 
    lg: "h-12 w-12 text-base"
  };

  const initials = fallbackText || getInitials(name, email, singleInitial);
  const colorSeed = name || email || fallbackText;
  const avatarColor = getAvatarColor(colorSeed);

  return (
    <Avatar className={cn(sizeClasses[size], className)}>
      <AvatarImage 
        src={src || undefined} 
        alt={alt}
      />
      <AvatarFallback 
        className="font-semibold text-white border border-border/10"
        style={{ backgroundColor: avatarColor }}
      >
        {initials ? (
          <span className="font-medium">
            {initials}
          </span>
        ) : (
          <User className="h-4 w-4" />
        )}
      </AvatarFallback>
    </Avatar>
  );
}